import { FormInstance } from "antd";
import React, { useEffect, useState } from "react";
import { AppTextInput } from "components/inputs";
import { SelectedCallRecordDraft } from "pages/callRecords/callRecords";
import styled from "styled-components";

const ValidationText = styled.div`
  font-size: 11px;
  color: #ff4d4f;
  margin-top: 4px;
  margin-left: 4px;
`;

export interface CallRecordDurationProps {
  readonly duration?: string;
}

interface AddCallRecordFormProps {
  readonly value?: CallRecordDurationProps;
  readonly onChange?: (value: CallRecordDurationProps) => void;
  readonly clearForm: boolean;
  readonly selectedDraft?: SelectedCallRecordDraft;
  readonly form?: FormInstance;
  readonly disabled?: boolean;
}

export const CallRecordDuration: React.FC<AddCallRecordFormProps> = ({
  value,
  onChange,
  clearForm,
  selectedDraft,
  form,
  disabled
}) => {
  const [duration, setDuration] = useState<string>("");
  const [error, setError] = useState<string>("");

  const formatSecondsToMMSS = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Populate from draft
  useEffect(() => {
    if (selectedDraft?.duration !== undefined) {
      const formatted = formatSecondsToMMSS(Number(selectedDraft.duration));
      setDuration(formatted);
      form?.setFieldsValue({
        duration: { duration: formatted }
      });
    }
  }, [selectedDraft]);

  const onDurationChange = (input: string) => {
    // Allow only numbers and colon
    if (!/^[0-9:]*$/.test(input)) return;

    // Enforce seconds <= 59 if colon exists
    if (input.includes(":")) {
      const [mins, secs] = input.split(":");

      // Allow partial typing like "12:" or "12:3"
      if (secs && Number(secs) > 59) {
        setError("Seconds must be between 0 and 59");
        return;
      }
      setError("");
    }

    setDuration(input);

    form?.setFieldsValue({
      duration: { duration: input }
    });

    onChange?.({
      ...(value ?? {}),
      duration: input
    });
  };

  // Clear form
  useEffect(() => {
    if (clearForm) {
      setDuration("");
      form?.setFieldsValue({
        duration: { duration: "" }
      });
    }
  }, [clearForm]);

  return (
    <div>
      <AppTextInput
        label="Duration"
        value={duration}
        onChange={onDurationChange}
        placeholder="mm:ss"
        disabled={disabled}
      />
      {error && <ValidationText>{error}</ValidationText>}
    </div>
  );
};
